import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactTooltip from "react-tooltip";

import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  IconButton,
  Menu,
  MenuItem,
  Table as MuiTable,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useModals } from "hooks";
import { useDealActions, useMeasuresSelector } from "store";
import { Measure } from "store/types";

type SelectedProperties = Pick<
  Measure,
  | "name"
  | "formula"
  | "calc_formula_cross_round"
  | "is_global"
  | "is_global_value"
  | "is_absolute"
  | "reduce_method_cross_round"
  | "reduce_method_cross_time"
  | "reduce_method_cross_deal"
  | "numerify"
  | "is_negative"
  | "postfix"
  | "is_currency"
  | "prime_role"
> & { measure_class: string }; // Mixed property

type SelectedKeys = keyof SelectedProperties;

type PropertyTitle = Record<SelectedKeys, string>;

const propertyTitle: PropertyTitle = {
  name: "Measure",
  formula: "Calculations",
  calc_formula_cross_round: "Calculate formula cross rounds",
  is_global: "Is Global",
  is_global_value: "Is Global Value",
  is_absolute: "Is Absolute",
  reduce_method_cross_round: "Reduce Method Cross Round",
  reduce_method_cross_time: "Reduce Method Cross Time",
  reduce_method_cross_deal: "Reduce Method Cross Deal",
  numerify: "Numerify",
  is_negative: "Is Negative",
  postfix: "Postfix",
  is_currency: "Is Currency",
  prime_role: "Prime role",
  measure_class: "Measure Class" // Mixed property
};

export interface Props {
  setMeasure: (item: Measure) => void;
  measuresGroups?: { id: number; name: string }[];
  searchValue: string;
  onDuplicate: (
    item: Measure & {
      measure_class: string;
    }
  ) => void;
}

export const Table: React.FC<Props> = ({ setMeasure, measuresGroups, searchValue, onDuplicate }) => {
  const measures = useMeasuresSelector();

  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    measure: Measure & {
      measure_class: string;
    };
  } | null>(null);

  const { deleteModal } = useModals();

  const { getMeasureParametersAction, deleteMeasureAction } = useDealActions();

  useEffect(() => {
    return () => {
      getMeasureParametersAction();
    };
  }, []);

  const handleDelete = useCallback(
    (id: number) => {
      deleteModal({
        action: () => deleteMeasureAction(id),
        message: "Are you sure you want to archive measure?",
        element: "ARCHIVE MEASURE",
        onPressSuccess: "ARCHIVE"
      });
    },
    [deleteModal]
  );

  const handleCloseRowMenu = () => setRowMenu(null);

  const tableBody: Array<Measure & { measure_class: string }> = useMemo(() => {
    const rows = measures.map(({ measure_group_id, ...rest }) => ({
      measure_group_id,
      measure_class: measuresGroups?.find(({ id }) => id === measure_group_id)?.name || "",
      ...rest
    }));

    if (searchValue) {
      return rows.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase().trim()));
    }

    return rows;
  }, [measures, searchValue, measuresGroups]);

  return (
      <Stack sx={{ p: "16px", borderRadius: "8px", overflow: "auto", bgcolor: "white", boxShadow: "0 1px 2px 0 #1212171a", margin: "0 2px 2px 2px" }}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          height: "100%",
          border: "1px solid #dae0e2",
          borderTop: "none",
        }}
      >
        <MuiTable
          sx={{
            ".MuiTableCell-root": {
              ":first-child": {
                position: "sticky",
                left: 0,
                zIndex: 3
              }
            },
            ".MuiTableCell-body": {
              padding: "14px 26px",
              transition: "all 0.2s linear",
              backgroundColor: "#f5f7f7",
              ":last-child": { width: "4%" },
              ":first-child": {
                minWidth: "200px",
                maxWidth: "200px",
                padding: "14px 0px 14px 17px",
                zIndex: 1,
                cursor: "pointer"
              },
              position: "relative"
            }
          }}
          stickyHeader
        >
          <TableHead sx={{ bgcolor: "#edf1f9" }}>
            <TableRow sx={{ height: "44px" }}>
              {Object.values(propertyTitle).map((item: string, index: number) => (
                <TableCell align={index ? "center" : "left"} sx={commonHeadCellSx} key={index}>
                  {item}
                </TableCell>
              ))}
              <TableCell sx={commonHeadCellSx} />
              <TableCell sx={commonHeadCellSx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {tableBody.map((row) => (
              <TableRow
                sx={{
                  height: "44px",
                  ".MuiButtonBase-root": { opacity: 0, transition: "all 0.3s linear" },
                  ":hover": {
                    ".MuiTableCell-body": { backgroundColor: "#dae0e2" },
                    ".MuiButtonBase-root": { opacity: 1 }
                  }
                }}
                key={row.id}
              >
                {(Object.keys(propertyTitle) as SelectedKeys[]).map((cellKey, index) => (
                  <TableCell
                    align={index ? "center" : "left"}
                    sx={commonBodyCellSx}
                    key={index}
                    {...(!index && { onClick: () => setMeasure(row) })}
                  >
                    <>{`${row[cellKey]}`}</>
                  </TableCell>
                ))}
                <TableCell sx={commonBodyCellSx}>
                  {!row.is_global && (
                    <IconButton onClick={() => handleDelete(row.id)}>
                      <ArchiveOutlinedIcon sx={{ height: 18, width: 18, fill: "#173c4c" }} />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell sx={commonBodyCellSx}>
                  <IconButton onClick={(e) => setRowMenu({ anchor: e.currentTarget, measure: row })}>
                    <MoreHorizIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <ReactTooltip className="tooltip" place="top" effect="solid" type="light" />
      {rowMenu && (
        <Menu id="basic-menu" anchorEl={rowMenu.anchor} open={Boolean(rowMenu.anchor)} onClose={handleCloseRowMenu}>
          <MenuItem
            onClick={() => {
              onDuplicate(rowMenu.measure);
              handleCloseRowMenu();
            }}
          >
            Duplicate measure
          </MenuItem>
        </Menu>
      )}
    </Stack>
  );
};

const commonBodyCellSx: TableCellProps["sx"] = {
  color: "#013951",
  position: "relative",
  borderLeft: "4px solid transparent",
  padding: "0 23px",
  verticalAlign: "middle",
  fontSize: "15px",
  border: "none",
  borderBottom: "1px solid #bbc5c7",
  minWidth: "140px",
  maxWidth: "240px"
};

const commonHeadCellSx: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "16px",
  padding: "0 23px !important",
  verticalAlign: "middle",
  minWidth: "140px",
  maxWidth: "240px",
  fontWeight: "normal",
  border: "none"
};
